import { ImageFit, ImageFrame } from '../../listing-image/listing-image.model';

export type EditorTool = 'crop' | 'markup';
export type MarkupTool = 'select' | 'pen' | 'arrow' | 'rect' | 'ellipse' | 'text';
//draggable points of a selected annotation: arrow ends, rectangle/ellipse corners
export type AnnotationHandle = 'from' | 'to' | 'tl' | 'tr' | 'bl' | 'br';
export type AspectMode = 'free' | '4:3';
export type SizePreset = 'S' | 'M' | 'L';
export type OrientationOp = 'cw' | 'ccw' | 'flipH' | 'flipV';

export interface Point { x: number; y: number; }
export interface Rect { x: number; y: number; w: number; h: number; }

//all coordinates are in oriented image pixels (after rotation/flip, before crop)
export type Annotation =
    | { kind: 'pen'; color: string; width: number; points: Point[] }
    | { kind: 'arrow' | 'rect' | 'ellipse'; color: string; width: number; from: Point; to: Point }
    | { kind: 'text'; color: string; size: number; at: Point; text: string };

export interface EditorState {
    quarterTurns: number; //clockwise, 0..3, applied before the flips
    flipH: boolean;
    flipV: boolean;
    crop: Rect;
    annotations: Annotation[];
    listingFrame: Rect | null; //4:3 section inside the crop for the listing boxes, null = whole image centered
    fit: ImageFit;
}

//crop & rotate tab: cut the photo or only set its listing frame (file unchanged)
export type CropTarget = 'photo' | 'frame';

export interface ImageEditorData {
    file: File;
    title?: string;
    frame?: ImageFrame | null; //current listing frame (relative to the file)
    fit?: ImageFit;
    listingFrameEnabled?: boolean; //only images of a public listing have a listing frame
}

//file undefined: only frame/fit changed (the original file stays). frame is relative to the (new) file.
export interface ImageEditorResult {
    file?: File;
    asCopy: boolean;
    frame: ImageFrame | null;
    fit: ImageFit;
}

export const ASPECT_RATIOS: Record<AspectMode, number | undefined> = { 'free': undefined, '4:3': 4 / 3 };
export const MARKUP_COLORS = ['#e53935', '#fdd835', '#43a047', '#1e88e5', '#ffffff', '#000000'];
//relative to the longer image side, so markup looks the same on every image size
const STROKE_FACTOR: Record<SizePreset, number> = { S: 0.004, M: 0.008, L: 0.014 };
const TEXT_FACTOR: Record<SizePreset, number> = { S: 0.03, M: 0.045, L: 0.065 };

export function strokeWidth(size: SizePreset, imageW: number, imageH: number): number {
    return Math.max(2, Math.max(imageW, imageH) * STROKE_FACTOR[size]);
}

export function textSize(size: SizePreset, imageW: number, imageH: number): number {
    return Math.max(12, Math.max(imageW, imageH) * TEXT_FACTOR[size]);
}

export function textFont(size: number): string {
    return `600 ${size}px Roboto, Arial, sans-serif`;
}

export function orientedSize(w: number, h: number, quarterTurns: number): { w: number; h: number } {
    return quarterTurns % 2 === 0 ? { w, h } : { w: h, h: w };
}

//maps a point of an oriented image of size W x H into the image after applying op
export function mapPoint(p: Point, op: OrientationOp, W: number, H: number): Point {
    switch (op) {
        case 'cw': return { x: H - p.y, y: p.x };
        case 'ccw': return { x: p.y, y: W - p.x };
        case 'flipH': return { x: W - p.x, y: p.y };
        case 'flipV': return { x: p.x, y: H - p.y };
    }
}

export function mapRect(r: Rect, op: OrientationOp, W: number, H: number): Rect {
    return normalizeRect(mapPoint({ x: r.x, y: r.y }, op, W, H), mapPoint({ x: r.x + r.w, y: r.y + r.h }, op, W, H));
}

export function mapAnnotation(a: Annotation, op: OrientationOp, W: number, H: number): Annotation {
    switch (a.kind) {
        case 'pen': return { ...a, points: a.points.map(p => mapPoint(p, op, W, H)) };
        case 'text': return { ...a, at: mapPoint(a.at, op, W, H) };
        default: return { ...a, from: mapPoint(a.from, op, W, H), to: mapPoint(a.to, op, W, H) };
    }
}

export function normalizeRect(a: Point, b: Point): Rect {
    return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) };
}

//largest rect with the given ratio, centered inside bounds
export function fitAspect(bounds: Rect, ratio: number): Rect {
    let w = bounds.w;
    let h = w / ratio;
    if (h > bounds.h) {
        h = bounds.h;
        w = h * ratio;
    }
    return { x: bounds.x + (bounds.w - w) / 2, y: bounds.y + (bounds.h - h) / 2, w, h };
}

//draws the source image rotated (clockwise quarter turns) and then flipped
export function renderOriented(source: CanvasImageSource & { width: number; height: number }, quarterTurns: number, flipH: boolean, flipV: boolean): HTMLCanvasElement {
    const size = orientedSize(source.width, source.height, quarterTurns);
    const canvas = document.createElement('canvas');
    canvas.width = size.w;
    canvas.height = size.h;
    const ctx = canvas.getContext('2d')!;
    ctx.translate(size.w / 2, size.h / 2);
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    ctx.rotate(quarterTurns * Math.PI / 2);
    ctx.drawImage(source, -source.width / 2, -source.height / 2);
    return canvas;
}

//contrasting outline so markup stays readable on light and dark image areas
function outlineColor(color: string): string {
    return color === '#000000' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.45)';
}

export function drawAnnotation(ctx: CanvasRenderingContext2D, a: Annotation): void {
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const outline = outlineColor(a.color);
    if (a.kind === 'text') {
        ctx.font = textFont(a.size);
        ctx.textBaseline = 'top';
        ctx.lineWidth = a.size * 0.15;
        ctx.strokeStyle = outline;
        ctx.strokeText(a.text, a.at.x, a.at.y);
        ctx.fillStyle = a.color;
        ctx.fillText(a.text, a.at.x, a.at.y);
    } else {
        //outline pass first, then the colored pass on top
        drawShape(ctx, a, outline, a.width * 1.8);
        drawShape(ctx, a, a.color, a.width);
    }
    ctx.restore();
}

function drawShape(ctx: CanvasRenderingContext2D, a: Exclude<Annotation, { kind: 'text' }>, style: string, lineWidth: number): void {
    ctx.strokeStyle = style;
    ctx.fillStyle = style;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    switch (a.kind) {
        case 'pen': {
            const pts = a.points;
            if (pts.length === 1) {
                ctx.arc(pts[0].x, pts[0].y, lineWidth / 2, 0, Math.PI * 2);
                ctx.fill();
                return;
            }
            //smooth the stroke with quadratic curves through the midpoints
            ctx.moveTo(pts[0].x, pts[0].y);
            for (let i = 1; i < pts.length - 1; i++) {
                ctx.quadraticCurveTo(pts[i].x, pts[i].y, (pts[i].x + pts[i + 1].x) / 2, (pts[i].y + pts[i + 1].y) / 2);
            }
            ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
            ctx.stroke();
            return;
        }
        case 'rect': {
            const r = normalizeRect(a.from, a.to);
            ctx.rect(r.x, r.y, r.w, r.h);
            ctx.stroke();
            return;
        }
        case 'ellipse': {
            const r = normalizeRect(a.from, a.to);
            ctx.ellipse(r.x + r.w / 2, r.y + r.h / 2, r.w / 2, r.h / 2, 0, 0, Math.PI * 2);
            ctx.stroke();
            return;
        }
        case 'arrow': {
            const angle = Math.atan2(a.to.y - a.from.y, a.to.x - a.from.x);
            const headLength = Math.max(a.width * 4, 10);
            const spread = Math.PI / 6.5;
            //shaft ends inside the head so the round line cap does not poke through the tip
            ctx.moveTo(a.from.x, a.from.y);
            ctx.lineTo(a.to.x - Math.cos(angle) * headLength * 0.7, a.to.y - Math.sin(angle) * headLength * 0.7);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(a.to.x, a.to.y);
            ctx.lineTo(a.to.x - headLength * Math.cos(angle - spread), a.to.y - headLength * Math.sin(angle - spread));
            ctx.lineTo(a.to.x - headLength * Math.cos(angle + spread), a.to.y - headLength * Math.sin(angle + spread));
            ctx.closePath();
            const headOutline = lineWidth - a.width; //head outline only in the outline pass
            if (headOutline > 0) {
                ctx.lineWidth = headOutline;
                ctx.stroke();
            }
            ctx.fill();
            return;
        }
    }
}

//#region selecting and changing annotations (textWidth: measured width of a text annotation)
export function annotationBounds(a: Annotation, textWidth: (a: Extract<Annotation, { kind: 'text' }>) => number): Rect {
    switch (a.kind) {
        case 'pen': {
            const xs = a.points.map(p => p.x);
            const ys = a.points.map(p => p.y);
            return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
        }
        case 'text':
            return { x: a.at.x, y: a.at.y, w: textWidth(a), h: a.size * 1.15 };
        default:
            return normalizeRect(a.from, a.to);
    }
}

//whether p (image pixels) is on the annotation: on its line within tolerance, for text inside its box
export function hitAnnotation(a: Annotation, p: Point, tolerance: number, textWidth: (a: Extract<Annotation, { kind: 'text' }>) => number): boolean {
    switch (a.kind) {
        case 'text': {
            const b = annotationBounds(a, textWidth);
            return p.x >= b.x - tolerance && p.x <= b.x + b.w + tolerance && p.y >= b.y - tolerance && p.y <= b.y + b.h + tolerance;
        }
        case 'pen': {
            const t = tolerance + a.width / 2;
            if (a.points.length === 1) return Math.hypot(p.x - a.points[0].x, p.y - a.points[0].y) <= t;
            return a.points.some((q, i) => i > 0 && distanceToSegment(p, a.points[i - 1], q) <= t);
        }
        case 'arrow':
            return distanceToSegment(p, a.from, a.to) <= tolerance + a.width / 2;
        case 'rect': {
            const r = normalizeRect(a.from, a.to);
            const corners = [{ x: r.x, y: r.y }, { x: r.x + r.w, y: r.y }, { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h }];
            return corners.some((c, i) => distanceToSegment(p, c, corners[(i + 1) % 4]) <= tolerance + a.width / 2);
        }
        case 'ellipse': {
            const r = normalizeRect(a.from, a.to);
            const rx = Math.max(r.w / 2, 1);
            const ry = Math.max(r.h / 2, 1);
            //approximate distance to the ellipse outline
            const d = Math.abs(Math.hypot((p.x - r.x - rx) / rx, (p.y - r.y - ry) / ry) - 1) * Math.min(rx, ry);
            return d <= tolerance + a.width / 2;
        }
    }
}

export function translateAnnotation(a: Annotation, dx: number, dy: number): Annotation {
    const move = (p: Point): Point => ({ x: p.x + dx, y: p.y + dy });
    switch (a.kind) {
        case 'pen': return { ...a, points: a.points.map(move) };
        case 'text': return { ...a, at: move(a.at) };
        default: return { ...a, from: move(a.from), to: move(a.to) };
    }
}

export function annotationHandles(a: Annotation): { handle: AnnotationHandle; point: Point }[] {
    if (a.kind === 'arrow') return [{ handle: 'from', point: a.from }, { handle: 'to', point: a.to }];
    if (a.kind === 'rect' || a.kind === 'ellipse') {
        const r = normalizeRect(a.from, a.to);
        return [
            { handle: 'tl', point: { x: r.x, y: r.y } }, { handle: 'tr', point: { x: r.x + r.w, y: r.y } },
            { handle: 'bl', point: { x: r.x, y: r.y + r.h } }, { handle: 'br', point: { x: r.x + r.w, y: r.y + r.h } }
        ];
    }
    return [];
}

//drags one handle to p: arrow end, or a corner with the opposite corner fixed
export function moveHandle(a: Annotation, handle: AnnotationHandle, p: Point): Annotation {
    if (a.kind === 'arrow') return handle === 'from' ? { ...a, from: p } : { ...a, to: p };
    if (a.kind === 'rect' || a.kind === 'ellipse') {
        const r = normalizeRect(a.from, a.to);
        const opposite: Record<string, Point> = {
            tl: { x: r.x + r.w, y: r.y + r.h }, tr: { x: r.x, y: r.y + r.h },
            bl: { x: r.x + r.w, y: r.y }, br: { x: r.x, y: r.y }
        };
        return { ...a, from: opposite[handle], to: p };
    }
    return a;
}

function distanceToSegment(p: Point, a: Point, b: Point): number {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSquared = dx * dx + dy * dy;
    const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}
//#endregion
